// The queue's detail in words (queue/detail.rs): what each proposal waits
// on, how long comparable checks took and how far to trust that, and its
// approval as its landing says it, apart from the forecast. Unknown is said
// as unknown, never filled in.
import type { ApprovalActual, ApprovalForecast, CheckHistory, QueueCheck, QueueWait } from '../api/types'
import { reasonText } from './words'

/** A duration, short: `<1 s`, `45 s`, `12 min`, `2 h 5 min`. */
export function duration(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000))
  if (seconds < 1) return '<1 s'
  if (seconds < 60) return `${seconds} s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  return minutes % 60 ? `${hours} h ${minutes % 60} min` : `${hours} h`
}

/** A check's purpose, as people name it. */
export const PURPOSES = { landing: 'Landing check', proposal: 'Proposal check', hand: 'Requested check', idle_main: 'Main check' } as const

/**
 * A check whose job ended: its last report as the runner sent it, never
 * extended. Its times are final only when that report says it finished.
 */
export function endedText(check: QueueCheck): string {
  const ended = `${PURPOSES[check.purpose]} ended (${check.state})`
  const progress = check.progress
  if (!progress) return `${ended}: no measurement reported`
  const parts = progress.total > 0 ? `${progress.completed}/${progress.total} ${progress.total === 1 ? 'part' : 'parts'}` : 'no parts'
  const activity = progress.activity
  if (!activity) return `${ended} · last report: ${parts}, ${duration(progress.elapsed_ms)} in all · final time unknown`
  const times = `waited ${duration(activity.wait_ms)}, executed ${duration(activity.execution_ms)}`
  return activity.phase === 'finished' ? `${ended} · ${parts} · ${times}` : `${ended} · last report: ${parts}, ${times} · final time unknown`
}

/** How long ago `at` was, from the server's `measured_at`; empty when unknown. */
export function since(at: string | null, measuredAt: string): string {
  const then = at ? Date.parse(at) : NaN
  const now = Date.parse(measuredAt)
  return Number.isFinite(then) && Number.isFinite(now) ? duration(now - then) : ''
}

/** A runner's hint, bounded plain text: it is not Poulpe's. */
function hint(text: string): string {
  const plain = text.replace(/\s+/g, ' ').replace(/[\u0000-\u001f\u007f-\u009f‪-‮⁦-⁩]/g, '').trim()
  return plain.length > 120 ? `…${plain.slice(-119)}` : plain
}

/** What the queue makes a proposal wait on, in words. */
export function waitText(wait: QueueWait): string {
  switch (wait.on) {
    case 'head': return wait.note ? `Its head to reach GitHub: ${hint(wait.note)}` : 'Its head to reach GitHub'
    case 'queue': return 'The queue’s next step'
    case 'runner': return `Its runner to start the check (job ${wait.state})`
    case 'admission': return wait.holder ? `Its runner, busy with: ${hint(wait.holder)}` : 'Its runner, busy with other work'
    case 'check': return 'Its check, running'
    case 'outcome': return 'Its runner to report the check’s outcome'
    case 'review': return wait.note ? `Its review: ${hint(wait.note)}` : 'Its review'
    case 'landing': return 'Another landing: one runs at a time'
    case 'batch': return 'A batch a person picked, which goes first'
    case 'held': return 'A person: held since its batch did not land'
    case 'forge': return 'GitHub: reading or moving main'
    case 'candidate': return wait.build ? 'Its candidate, being built' : 'Its candidate'
    case 'approval': return wait.deferred ? 'A person’s passkey approval; deferred, other work lands meanwhile' : 'A person’s passkey approval'
    case 'resume': return 'Its turn to resume: approved while deferred'
  }
}

/**
 * How long comparable runs took, and how far that predicts the next one: the
 * range shows a five-second warm run beside a cold build of the same work.
 */
export function historyText(history: CheckHistory): string {
  const unpassed = [
    ...history.failed ? [`${history.failed} failed`] : [],
    ...history.unsuccessful ? [`${history.unsuccessful} ended without a result`] : [],
  ]
  if (!history.execution) {
    return unpassed.length ? `No comparable run passed (${unpassed.join(', ')})` : 'No comparable run yet'
  }
  const { min_ms, median_ms, max_ms } = history.execution
  const [fastest, slowest] = [duration(min_ms), duration(max_ms)]
  const range = fastest === slowest ? fastest : `${fastest}–${slowest}`
  const facts = [
    `${history.runs} ${history.runs === 1 ? 'run' : 'runs'}`,
    ...history.basis === 'same_purpose' ? ['any work'] : [],
    ...unpassed,
  ]
  const trust = history.confidence === 'steady' ? '' : ' · low confidence'
  const wait = history.wait && history.wait.max_ms > 0 ? ` · usual wait ${duration(history.wait.median_ms)}` : ''
  return `Usually ${duration(median_ms)} (${range}; ${facts.join(', ')})${trust}${wait}`
}

/** What the landing says of a person's approval. */
export function actualText(actual: ApprovalActual, measuredAt: string): string {
  switch (actual.state) {
    case 'undecided': return 'Not decided yet'
    case 'requested': {
      const waited = since(actual.since, measuredAt)
      const more = actual.reasons_total > 1 ? ` (and ${actual.reasons_total - 1} more)` : ''
      const why = actual.reasons[0] ? ` ${reasonText(actual.reasons[0])}${more}` : ''
      const deferred = actual.deferred_at ? ` Deferred ${since(actual.deferred_at, measuredAt)} ago; other work may land.` : ''
      return `Asked of a person${waited ? ` ${waited} ago` : ''}.${why}${deferred}`
    }
    case 'satisfied': return actual.resume_at ? 'Approved by a person while deferred: it resumes in its turn' : 'Approved by a person'
    case 'not_required': return 'Not needed: the policy lets it land'
  }
}

/** The forecast, said as one, with what it could not read. */
export function forecastText(forecast: ApprovalForecast): string {
  if (forecast.needs_person === true) {
    const more = forecast.reasons_total > 1 ? ` (and ${forecast.reasons_total - 1} more)` : ''
    return `Forecast: likely needs a person. ${forecast.reasons[0] ? reasonText(forecast.reasons[0]) : ''}${more}`.trim()
  }
  if (forecast.needs_person === false) return 'Forecast: likely lands without a person'
  const missing = [
    ...forecast.diff === 'missing' ? ['no copy of its diff yet'] : forecast.diff === 'truncated' ? ['its diff copy is cut'] : [],
    ...forecast.definitions === 'unknown' ? ['its check definitions are not known yet'] : [],
  ]
  return `Forecast: unknown${missing.length ? ` (${missing.join('; ')})` : ''}`
}
